import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';

import { useStopMeasureWhenRendered } from '@mint-lib/datadog';
import {
  useIsAllowedPermission,
  usePermissions,
} from '@mint-lib/profile-helpers';
import styled, { css } from '@mint-lib/styled';

import { Box, Input } from '@myn/mui';

import { useCellValueUpdate } from '../../../../../../hooks/useCellValueUpdate.js';
import { TRACKING_EVENTS } from '../../../../../../types/performanceTracking.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import NameTextField from './NameTextField.jsx';
import PredictivePlanningRowNameField from './PredictivePlanningRowNameField.jsx';

type Props = {
  value: string;
  column: BuilderTableColumn;
  row: BuilderTableRow;
};

function NameInput({ value, column, row }: Props) {
  const {
    isBuyingPlanLocked,
    isCampaignCurrencyMode,
    isPredictivePlanningMode,
    isArchivedVersion,
    scale,
  } = row.original;

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(value ?? '');

  const inputRef = useRef<HTMLInputElement>(null);

  const isAllowed = useIsAllowedPermission('campaignPlanningFeature:modify');
  const { getPermissionField } = usePermissions();
  const isNonFinancialUser = !getPermissionField('financialDataFeature');

  const isDisabled =
    !isAllowed ||
    isPredictivePlanningMode ||
    isNonFinancialUser ||
    isArchivedVersion ||
    isCampaignCurrencyMode ||
    isBuyingPlanLocked;
  const isEditIconShown = !isFocused && !isDisabled;

  const { startMeasurePerformance } = column;

  const { updateCellValue } = useCellValueUpdate({
    column,
    row,
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) =>
    setInputValue(e.target.value);

  const handleInputValueChange = (newValue: string) => {
    setInputValue(newValue);
    setIsFocused(false);
  };

  const handleBlur = async () => {
    const val = String(value);

    handleInputValueChange(inputValue);

    if (val !== inputValue) {
      startMeasurePerformance(TRACKING_EVENTS.TABLE_UPDATE);

      const newValue = await updateCellValue({
        currentValue: val,
        newValue: inputValue,
      });
      handleInputValueChange(String(newValue));
    }
  };

  const handleFocus = () => {
    if (isDisabled) {
      return;
    }

    setIsFocused(true);
  };

  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    if (isFocused && inputRef.current) {
      startMeasurePerformance(TRACKING_EVENTS.VALUE_INSERTION);
      inputRef.current.focus();
      globalThis.addEventListener('keydown', handleKeyDown);

      return () => {
        globalThis.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isFocused]);

  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  useStopMeasureWhenRendered(
    TRACKING_EVENTS.VALUE_INSERTION,
    {
      react: { component: 'NameField' },
    },
    { shouldIgnoreRender: isFocused },
  );

  if (isPredictivePlanningMode) {
    return (
      <PredictivePlanningRowNameField
        value={value}
        width={Number(column.width)}
        scale={scale}
      />
    );
  }

  return (
    <InputWrapper>
      {!isFocused ? (
        <NameTextField
          handleFocus={handleFocus}
          inputValue={inputValue}
          canBeEdited={isEditIconShown}
          width={Number(column.width)}
          scale={scale}
        />
      ) : (
        <StyledInput
          type="text"
          size="medium"
          inputRef={inputRef}
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          readOnly={isDisabled}
          $scale={scale}
          $isFocused={isFocused}
        />
      )}
    </InputWrapper>
  );
}

const InputWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  overflow: hidden;
  & > span > div,
  & > div {
    height: 100%;
  }
`;

const StyledInput = styled(Input)<{
  $scale: number;
  $isFocused: boolean;
}>`
  ${({ $scale, $isFocused, theme, readOnly }) => css`
    min-height: 0;
    width: 100%;
    height: 100%;
    cursor: default;
    background: transparent;
    color: ${theme.palette.text.link};

    &:hover {
      cursor: ${$isFocused ? 'default' : 'pointer'};
      background: ${$isFocused ? 'transparent' : theme.palette.ui.hoverCell};

      input {
        cursor: ${$isFocused ? 'default' : 'pointer'};
      }

      .edit-button {
        display: ${readOnly ? 'none' : 'block'};
      }
    }

    &:not(.Mui-focused) {
      box-shadow: none;
      border-radius: unset !important;
    }

    input {
      user-select: none;
      cursor: default;
    }

    &.Mui-disabled {
      box-shadow: none;
      background-color: unset;
    }

    &.Mui-focused {
      color: ${!readOnly && theme.palette.text.primary};
    }

    font-size: ${0.875 * $scale}rem;
    line-height: ${1.25 * $scale};
  `}
`;

export default memo(NameInput);
